import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
    BarChart3, Calendar, MousePointerClick, Send,
    TrendingUp, Users, Target, ExternalLink, Play, Volume2, MessageSquare,
    Layers, X,
} from 'lucide-react';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
    ResponsiveContainer, Legend,
} from 'recharts';

import {
    getClickSummary, getBroadcastsReport, getFunnelsReport, getClickTimeline,
    type BroadcastReportRow, type FunnelReportRow,
    type PreviewBlock,
} from '@/services/api/click-reports';

type Period = '24h' | '7d' | '30d' | '90d';

const periodToRange = (p: Period): { start: string; end: string } => {
    const now = new Date();
    const start = new Date(now);
    if (p === '24h') start.setDate(now.getDate() - 1);
    else if (p === '7d') start.setDate(now.getDate() - 7);
    else if (p === '30d') start.setDate(now.getDate() - 30);
    else start.setDate(now.getDate() - 90);
    return { start: start.toISOString(), end: now.toISOString() };
};

const fmtNumber = (n: number) => n.toLocaleString('pt-BR');
const fmtDate = (iso: string, withHour = false) => {
    const d = new Date(iso);
    return withHour
        ? d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
        : d.toLocaleDateString('pt-BR');
};

type ButtonFilter = 'all' | 'with' | 'without';

export default function ClickReports() {
    const [period, setPeriod] = useState<Period>('7d');
    const [tab, setTab] = useState<'broadcasts' | 'funnels'>('broadcasts');
    const [buttonFilter, setButtonFilter] = useState<ButtonFilter>('with');
    const [openGroup, setOpenGroup] = useState<BroadcastReportRow | null>(null);
    const range = useMemo(() => periodToRange(period), [period]);
    const bucket: 'hour' | 'day' = period === '24h' ? 'hour' : 'day';

    const { data: summary } = useQuery({
        queryKey: ['click-summary', range.start, range.end],
        queryFn: () => getClickSummary(range.start, range.end),
    });
    const { data: timeline = [] } = useQuery({
        queryKey: ['click-timeline', range.start, range.end, bucket],
        queryFn: () => getClickTimeline(range.start, range.end, bucket),
    });
    const withFlag = buttonFilter === 'with' ? true : buttonFilter === 'without' ? false : null;
    const { data: broadcasts = [] } = useQuery({
        queryKey: ['click-broadcasts', range.start, range.end, withFlag],
        queryFn: () => getBroadcastsReport(range.start, range.end, 50, withFlag),
        enabled: tab === 'broadcasts',
    });
    const { data: funnels = [] } = useQuery({
        queryKey: ['click-funnels', range.start, range.end],
        queryFn: () => getFunnelsReport(range.start, range.end),
        enabled: tab === 'funnels',
    });

    const t = summary?.totals;

    return (
        <div className="p-6 space-y-6">
            {/* Header */}
            <div className="bg-gradient-to-br from-[#0f172a] to-[#1e293b] rounded-2xl p-6 text-white flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <div className="bg-white/10 p-3 rounded-xl">
                        <BarChart3 className="w-6 h-6" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold">Relatórios de Cliques</h1>
                        <p className="text-sm text-white/60">
                            CTR de broadcasts e fluxos por período
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2 bg-white/10 rounded-lg p-1">
                    {(['24h', '7d', '30d', '90d'] as Period[]).map(p => (
                        <button
                            key={p}
                            onClick={() => setPeriod(p)}
                            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                                period === p
                                    ? 'bg-white text-[#0f172a]'
                                    : 'text-white/70 hover:text-white'
                            }`}
                        >
                            {p}
                        </button>
                    ))}
                </div>
            </div>

            {/* KPI cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <KpiCard
                    icon={<MousePointerClick className="w-5 h-5 text-blue-500" />}
                    label="Total de cliques"
                    value={fmtNumber(t?.clicks ?? 0)}
                    sub={`${fmtNumber(t?.unique_leads ?? 0)} leads únicos`}
                />
                <KpiCard
                    icon={<Send className="w-5 h-5 text-emerald-500" />}
                    label="Mensagens enviadas"
                    value={fmtNumber(t?.sent_broadcast ?? 0)}
                    sub={`${fmtNumber(t?.sent_with_buttons ?? 0)} com botão`}
                />
                <KpiCard
                    icon={<TrendingUp className="w-5 h-5 text-violet-500" />}
                    label="CTR (broadcast)"
                    value={`${(t?.ctr_broadcast ?? 0).toFixed(2)}%`}
                    sub={`${fmtNumber(t?.clicks_broadcast ?? 0)} cliques · base: msgs c/ botão`}
                />
                <KpiCard
                    icon={<Target className="w-5 h-5 text-amber-500" />}
                    label="Cliques em fluxos"
                    value={fmtNumber(t?.clicks_funnel ?? 0)}
                    sub="origem: funnel"
                />
            </div>

            {/* Timeline */}
            <div className="bg-white rounded-xl border border-gray-100 p-5">
                <div className="flex items-center gap-2 mb-4">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    <h3 className="text-sm font-semibold text-gray-700">
                        Cliques por {bucket === 'hour' ? 'hora' : 'dia'}
                    </h3>
                </div>
                <div className="h-72">
                    <ResponsiveContainer>
                        <LineChart data={timeline.map(b => ({
                            t: fmtDate(b.bucket, bucket === 'hour'),
                            broadcast: b.broadcast,
                            funnel: b.funnel,
                            total: b.total,
                        }))}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                            <XAxis dataKey="t" tick={{ fontSize: 11, fill: '#64748b' }} />
                            <YAxis tick={{ fontSize: 11, fill: '#64748b' }} />
                            <Tooltip />
                            <Legend wrapperStyle={{ fontSize: 12 }} />
                            <Line type="monotone" dataKey="broadcast" name="Broadcast" stroke="#3b82f6" strokeWidth={2} dot={false} />
                            <Line type="monotone" dataKey="funnel" name="Fluxo" stroke="#f59e0b" strokeWidth={2} dot={false} />
                            <Line type="monotone" dataKey="total" name="Total" stroke="#10b981" strokeWidth={1} strokeDasharray="4 4" dot={false} />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 border-b border-gray-200">
                <TabBtn active={tab === 'broadcasts'} onClick={() => setTab('broadcasts')}>
                    Broadcasts ({broadcasts.length})
                </TabBtn>
                <TabBtn active={tab === 'funnels'} onClick={() => setTab('funnels')}>
                    Fluxos ({funnels.length})
                </TabBtn>
            </div>

            {tab === 'broadcasts' && (
                <>
                    {/* Filtro com/sem botão */}
                    <div className="flex items-center gap-2 mb-4">
                        <span className="text-xs uppercase font-medium text-gray-500 mr-2">Filtro:</span>
                        {([
                            { v: 'with', label: 'Com botão (CTR útil)' },
                            { v: 'without', label: 'Sem botão' },
                            { v: 'all', label: 'Todos' },
                        ] as { v: ButtonFilter; label: string }[]).map(f => (
                            <button
                                key={f.v}
                                onClick={() => setButtonFilter(f.v)}
                                className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                                    buttonFilter === f.v
                                        ? 'bg-blue-50 text-blue-700 border border-blue-200'
                                        : 'bg-white text-gray-600 border border-gray-200 hover:border-gray-300'
                                }`}
                            >
                                {f.label}
                            </button>
                        ))}
                    </div>

                    {broadcasts.length === 0 ? (
                        <div className="bg-white rounded-xl border border-gray-100 p-12 text-center text-gray-400 text-sm">
                            Nenhum broadcast {buttonFilter === 'with' ? 'com botão ' : buttonFilter === 'without' ? 'sem botão ' : ''}no período
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {broadcasts.map(b => (
                                <BroadcastCard
                                    key={b.group_broadcast_id}
                                    b={b}
                                    onOpen={() => setOpenGroup(b)}
                                />
                            ))}
                        </div>
                    )}
                </>
            )}

            {openGroup && (
                <GroupDetailModal group={openGroup} onClose={() => setOpenGroup(null)} />
            )}

            {tab === 'funnels' && (
                <div className="space-y-3">
                    {funnels.map(f => (
                        <FunnelCard key={f.funnel_id} funnel={f} />
                    ))}
                    {funnels.length === 0 && (
                        <div className="bg-white rounded-xl border border-gray-100 p-12 text-center text-gray-400 text-sm">
                            Nenhum clique em fluxos no período
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function KpiCard({ icon, label, value, sub }: { icon: React.ReactNode; label: string; value: string; sub: string }) {
    return (
        <div className="bg-white rounded-xl border border-gray-100 p-5">
            <div className="flex items-center justify-between mb-3">
                <div className="bg-gray-50 p-2 rounded-lg">{icon}</div>
            </div>
            <p className="text-xs text-gray-500 uppercase font-medium tracking-wider">{label}</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
            <p className="text-xs text-gray-400 mt-1">{sub}</p>
        </div>
    );
}

function TabBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
    return (
        <button
            onClick={onClick}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                active ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
        >
            {children}
        </button>
    );
}

function BroadcastCard({ b, onOpen }: { b: BroadcastReportRow; onOpen: () => void }) {
    const ctrColor =
        !b.has_buttons ? 'bg-gray-100 text-gray-500' :
        b.ctr >= 10 ? 'bg-emerald-50 text-emerald-700' :
        b.ctr >= 3 ? 'bg-amber-50 text-amber-700' :
        'bg-gray-100 text-gray-600';

    const isGroup = b.pages_count > 1;

    return (
        <button
            type="button"
            onClick={onOpen}
            className="text-left bg-white rounded-xl border border-gray-100 overflow-hidden hover:shadow-md hover:border-blue-200 transition-all flex flex-col cursor-pointer"
        >
            {/* Preview tipo Messenger */}
            <div className="bg-gradient-to-br from-slate-100 to-slate-200 px-4 py-4 border-b border-gray-100 relative">
                {isGroup && (
                    <div className="absolute top-2 left-2 z-10">
                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase bg-violet-600/95 text-white px-2 py-0.5 rounded shadow-sm">
                            <Layers className="w-3 h-3" />
                            {b.pages_count} páginas
                        </span>
                    </div>
                )}
                <div className="space-y-2">
                    {b.blocks.map((blk, i) => <MessageBubble key={i} block={blk} />)}
                    {b.blocks.length === 0 && (
                        <div className="text-center py-6 text-gray-400 text-xs">
                            <MessageSquare className="w-5 h-5 mx-auto mb-1 opacity-50" />
                            sem preview
                        </div>
                    )}
                </div>
                <div className="flex justify-end mt-2">
                    {b.has_buttons ? (
                        <span className="text-[10px] font-semibold uppercase bg-blue-500/95 text-white px-2 py-0.5 rounded">
                            {b.buttons_count} {b.buttons_count === 1 ? 'botão' : 'botões'}
                        </span>
                    ) : (
                        <span className="text-[10px] font-semibold uppercase bg-gray-500/85 text-white px-2 py-0.5 rounded">
                            Sem botão
                        </span>
                    )}
                </div>
            </div>

            {/* Body */}
            <div className="p-4 space-y-3 flex-1 flex flex-col justify-between">
                <div>
                    <div className="flex items-start justify-between gap-2 mb-1">
                        <h4 className="font-semibold text-gray-800 text-sm truncate flex-1" title={b.title}>
                            {b.title}
                        </h4>
                        <span className="text-[11px] text-gray-400 shrink-0">
                            {isGroup ? `grp #${b.group_broadcast_id}` : `#${b.broadcast_id}`}
                        </span>
                    </div>
                    {isGroup && (
                        <p className="text-[11px] text-violet-600 font-medium">
                            Soma de {b.pages_count} páginas
                        </p>
                    )}
                </div>

                <div className="grid grid-cols-3 gap-2 pt-2 border-t border-gray-100">
                    <div>
                        <p className="text-[10px] text-gray-400 uppercase font-medium">Enviadas</p>
                        <p className="text-sm font-semibold text-gray-800">{fmtNumber(b.total_sent)}</p>
                    </div>
                    <div>
                        <p className="text-[10px] text-gray-400 uppercase font-medium">Cliques</p>
                        <p className="text-sm font-semibold text-blue-600">
                            {fmtNumber(b.clicks)}
                            {b.unique_clicks !== b.clicks && (
                                <span className="text-[10px] text-gray-400 font-normal ml-1">
                                    ({fmtNumber(b.unique_clicks)} u)
                                </span>
                            )}
                        </p>
                    </div>
                    <div>
                        <p className="text-[10px] text-gray-400 uppercase font-medium">CTR</p>
                        <span className={`inline-flex items-center text-xs font-semibold px-2 py-0.5 rounded-full ${ctrColor}`}>
                            {b.has_buttons ? `${b.ctr.toFixed(2)}%` : 'N/A'}
                        </span>
                    </div>
                </div>

                <div className="flex items-center justify-between">
                    <p className="text-[11px] text-gray-400">{fmtDate(b.created_at)}</p>
                    <span className="text-[11px] text-blue-600 font-medium">ver detalhes →</span>
                </div>
            </div>
        </button>
    );
}

function GroupDetailModal({ group, onClose }: { group: BroadcastReportRow; onClose: () => void }) {
    const isGroup = group.pages_count > 1;

    const ctrColor = (ctr: number, hasButtons: boolean) =>
        !hasButtons ? 'bg-gray-100 text-gray-500' :
        ctr >= 10 ? 'bg-emerald-50 text-emerald-700' :
        ctr >= 3 ? 'bg-amber-50 text-amber-700' :
        'bg-gray-100 text-gray-600';

    return (
        <div
            className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={onClose}
        >
            <div
                className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col"
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div className="bg-gradient-to-br from-[#0f172a] to-[#1e293b] px-6 py-5 text-white flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                            {isGroup && (
                                <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase bg-violet-500 text-white px-2 py-0.5 rounded">
                                    <Layers className="w-3 h-3" />
                                    Grupo · {group.pages_count} páginas
                                </span>
                            )}
                            <span className="text-[11px] text-white/60">
                                {isGroup ? `grp #${group.group_broadcast_id}` : `#${group.broadcast_id}`}
                            </span>
                        </div>
                        <h2 className="text-lg font-bold truncate" title={group.title}>{group.title}</h2>
                        <p className="text-xs text-white/60 mt-1">{fmtDate(group.created_at, true)}</p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="text-white/60 hover:text-white transition-colors shrink-0"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Totais do grupo */}
                <div className="px-6 py-4 bg-gray-50 border-b border-gray-100 grid grid-cols-4 gap-3">
                    <div>
                        <p className="text-[10px] text-gray-500 uppercase font-medium">Páginas</p>
                        <p className="text-lg font-bold text-gray-900">{fmtNumber(group.pages_count)}</p>
                    </div>
                    <div>
                        <p className="text-[10px] text-gray-500 uppercase font-medium">Enviadas (total)</p>
                        <p className="text-lg font-bold text-gray-900">{fmtNumber(group.total_sent)}</p>
                    </div>
                    <div>
                        <p className="text-[10px] text-gray-500 uppercase font-medium">Cliques</p>
                        <p className="text-lg font-bold text-blue-600">{fmtNumber(group.clicks)}</p>
                    </div>
                    <div>
                        <p className="text-[10px] text-gray-500 uppercase font-medium">CTR médio</p>
                        <p className="text-lg font-bold text-violet-600">
                            {group.has_buttons ? `${group.ctr.toFixed(2)}%` : 'N/A'}
                        </p>
                    </div>
                </div>

                {/* Lista por página */}
                <div className="flex-1 overflow-y-auto p-6">
                    <h3 className="text-xs uppercase font-semibold text-gray-500 mb-3">
                        Detalhamento por página
                    </h3>
                    <div className="space-y-2">
                        {group.pages.map(p => (
                            <div
                                key={p.broadcast_id}
                                className="flex items-center gap-4 px-4 py-3 bg-white border border-gray-100 rounded-lg hover:border-gray-200 transition-colors"
                            >
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-semibold text-gray-800 truncate" title={p.page_name}>
                                        {p.page_name}
                                    </p>
                                    <p className="text-[11px] text-gray-400">
                                        #{p.broadcast_id} {p.page_id ? `· page_id ${p.page_id}` : ''}
                                    </p>
                                </div>
                                <div className="grid grid-cols-4 gap-4 shrink-0">
                                    <div className="text-right">
                                        <p className="text-[10px] text-gray-400 uppercase font-medium">Enviadas</p>
                                        <p className="text-sm font-semibold text-gray-800">{fmtNumber(p.total_sent)}</p>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-[10px] text-gray-400 uppercase font-medium">Leads</p>
                                        <p className="text-sm font-semibold text-gray-600">{fmtNumber(p.total_leads)}</p>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-[10px] text-gray-400 uppercase font-medium">Cliques</p>
                                        <p className="text-sm font-semibold text-blue-600">
                                            {fmtNumber(p.clicks)}
                                            {p.unique_clicks !== p.clicks && (
                                                <span className="text-[10px] text-gray-400 font-normal ml-1">
                                                    ({fmtNumber(p.unique_clicks)} u)
                                                </span>
                                            )}
                                        </p>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-[10px] text-gray-400 uppercase font-medium">CTR</p>
                                        <span className={`inline-flex items-center text-xs font-semibold px-2 py-0.5 rounded-full ${ctrColor(p.ctr, p.has_buttons)}`}>
                                            {p.has_buttons ? `${p.ctr.toFixed(2)}%` : 'N/A'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

function MessageBubble({ block }: { block: PreviewBlock }) {
    const type = block.type;
    const text = block.message || block.title;
    const isMedia = ['image', 'photo', 'photo_template', 'video', 'video_template', 'audio'].includes(type);

    return (
        <div className="flex items-end gap-2">
            {/* Avatar fake */}
            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 shrink-0" />

            {/* Bubble */}
            <div className="bg-white rounded-2xl rounded-bl-md shadow-sm border border-gray-100 overflow-hidden max-w-[260px] flex-1">
                {/* Mídia */}
                {isMedia && block.media_url && (type === 'image' || type === 'photo' || type === 'photo_template') && (
                    <img src={block.media_url} alt="" className="w-full h-32 object-cover" />
                )}
                {isMedia && (type === 'video' || type === 'video_template') && (
                    <div className="w-full h-32 bg-gradient-to-br from-slate-700 to-slate-900 flex items-center justify-center">
                        <Play className="w-8 h-8 text-white/80" />
                    </div>
                )}
                {isMedia && type === 'audio' && (
                    <div className="w-full px-3 py-3 bg-slate-50 flex items-center gap-2 border-b border-gray-100">
                        <Volume2 className="w-4 h-4 text-blue-500" />
                        <div className="flex-1 h-1 bg-gray-200 rounded-full">
                            <div className="h-1 w-1/3 bg-blue-500 rounded-full" />
                        </div>
                        <span className="text-[10px] text-gray-400">áudio</span>
                    </div>
                )}

                {/* Texto */}
                {(text || block.subtitle) && (
                    <div className="px-3 py-2 space-y-0.5">
                        {block.title && type !== 'text' && (
                            <p className="text-sm font-semibold text-gray-800 line-clamp-2">{block.title}</p>
                        )}
                        {text && type === 'text' && (
                            <p className="text-sm text-gray-700 whitespace-pre-wrap line-clamp-3">{text}</p>
                        )}
                        {block.subtitle && (
                            <p className="text-xs text-gray-500 line-clamp-2">{block.subtitle}</p>
                        )}
                    </div>
                )}

                {/* Botões */}
                {block.buttons.length > 0 && (
                    <div className="border-t border-gray-100 divide-y divide-gray-100">
                        {block.buttons.slice(0, 3).map((btn, i) => (
                            <div key={i} className="px-3 py-2 text-center text-blue-600 text-xs font-medium hover:bg-blue-50 flex items-center justify-center gap-1">
                                {btn.type === 'url' && <ExternalLink className="w-3 h-3" />}
                                <span className="truncate">{btn.text}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function FunnelCard({ funnel }: { funnel: FunnelReportRow }) {
    return (
        <div className="bg-white rounded-xl border border-gray-100 p-5">
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                    <div className="bg-amber-50 p-2 rounded-lg">
                        <Users className="w-4 h-4 text-amber-500" />
                    </div>
                    <div>
                        <h4 className="font-semibold text-gray-800">{funnel.funnel_name}</h4>
                        <p className="text-xs text-gray-400">#{funnel.funnel_id}</p>
                    </div>
                </div>
                <div className="text-right">
                    <p className="text-2xl font-bold text-gray-800">{fmtNumber(funnel.total_clicks)}</p>
                    <p className="text-xs text-gray-400">cliques · {fmtNumber(funnel.unique_clicks)} únicos</p>
                </div>
            </div>
            <div className="space-y-2">
                {funnel.nodes.map(n => (
                    <div key={n.node_id ?? Math.random()} className="flex items-center justify-between p-2 bg-gray-50/50 rounded-lg">
                        <div>
                            <span className="text-sm font-medium text-gray-700">{n.node_name}</span>
                            {n.node_type && (
                                <span className="ml-2 text-[11px] text-gray-400 uppercase">{n.node_type}</span>
                            )}
                        </div>
                        <div className="text-sm">
                            <span className="font-semibold text-blue-600">{fmtNumber(n.clicks)}</span>
                            <span className="text-gray-400 text-xs ml-1">/ {fmtNumber(n.unique_clicks)} únicos</span>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
